import { injectable } from "@launchtray/tsyringe-async";
import { VariableSaver } from "../../../variableSaver/VariableSaver";

type ValueType = "string" | "number" | "boolean" | "object" | "map";

@injectable()
export class SaveModel {
    public static readonly GAME_PATH = "PeskyMoles";
    public constructor(
        private readonly variableSaver: VariableSaver
    ) {
    }

    public read<TResult>(owner: string, propertyKey: string, type: ValueType, defaultValue?: TResult): TResult {
        const key = this.getKey(owner, propertyKey);
        if (!this.variableSaver.exist(key)) {
            this.write(owner, propertyKey, type, defaultValue);
            return defaultValue;
        }
        let result: unknown;
        switch (type) {
            case "string":
                result = this.variableSaver.getString(key);
                break;
            case "number":
                result = this.variableSaver.getNumber(key);
                break;
            case "boolean":
                result = this.variableSaver.getBoolean(key);
                break;
            case "object":
                const json = this.variableSaver.getString(key);
                result = JSON.parse(json);
                break;
            case "map":
                const map = this.variableSaver.getString(key);
                result = new Map(JSON.parse(map));
                break;
            default:
                console.warn(`Unable to read ${key}, unsupported type ${type}`);
                break;
        }
        return (result ?? defaultValue) as TResult;
    }

    public write<TData>(owner: string, propertyKey: string, type: ValueType, value: TData): void {
        const key = this.getKey(owner, propertyKey);
        if (type != "map" && typeof value != type) {
            throw new Error(`Attempt to write type ${type} with unsupported value ${value}`);
        }
        switch (type) {
            case "string":
                this.variableSaver.saveString(key, value as string);
                return;
            case "number":
                this.variableSaver.saveNumber(key, value as number);
                return;
            case "boolean":
                this.variableSaver.saveBoolean(key, value as boolean);
                return;
            case "object":
                const json = JSON.stringify(value);
                this.variableSaver.saveString(key, json);
                return;
            case "map":
                const map = value as Map<any, any>;
                const str = JSON.stringify(Array.from(map));
                this.variableSaver.saveString(key, str);
                return;
            default:
                console.warn(`Unable to write ${key}, unsupported type ${type}`);
                return;
        }
    }

    private getKey(owner: string, propertyKey: string): string {
        return `${SaveModel.GAME_PATH}.${owner}.${propertyKey}`;
    }

    public clearGameStorage(): void {
        const keysToRemove: string[] = [];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(SaveModel.GAME_PATH)) {
                keysToRemove.push(key);
            }
        }

        keysToRemove.forEach((key) => localStorage.removeItem(key));
    }

}
